import { getDashboardById } from '@src/api/dashboards';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { SAVED_DASHBOARDS_BASE_KEY, DASHBOARD_TYPE, isAppModule } from '@utils/constants';
import { useDispatch } from 'react-redux';
import { get, set, isNumber } from 'lodash';
import { getRemoteFilters, mergeWithRemoteFilters, type Filter } from '@utils/filter';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { initialFilter } from '@store/common/reducer';
import { updateSaveData } from './atoms';
import { useNavigate } from 'react-router-dom';

const getFids = (filters: Filter[]) =>
  (filters || []).map((it) => it?.fid).filter(isNumber) as number[];

const updateFiltersForTrends = async (dashboardInfo: Record<string, any>) => {
  const appliedFiltersPath = 'config.state.common.appliedFilters';
  const summaryDrilldownDataPath = 'config.state.summary.drilldownData';
  const compareFiltersOnTrendsPath = 'config.state.module:trends.base.compareFilters';

  const filters = get(dashboardInfo, appliedFiltersPath, []);
  const compareFilters = get(dashboardInfo, compareFiltersOnTrendsPath, []);

  // collect all the filter id to get remote filter one time.
  const filterIds = getFids([
    ...(filters?.length ? filters : []),
    ...(compareFilters?.length ? compareFilters : []),
  ]);
  const remoteFilters = await getRemoteFilters(filterIds);

  // update compare filters
  const shouldUpdateCompareFilters = Array.isArray(compareFilters) && compareFilters.length;
  if (shouldUpdateCompareFilters) {
    set(
      dashboardInfo,
      compareFiltersOnTrendsPath,
      mergeWithRemoteFilters(compareFilters, remoteFilters),
    );
  }

  // Only update applied filter and drilldown filter when current applied filter is saved filter
  const shouldUpdateAppliedFilter = Boolean(filters?.length === 1 && isNumber(filters[0].fid));
  if (shouldUpdateAppliedFilter) {
    const currentAppliedFilter = filters[0];
    const newAppliedFilters = mergeWithRemoteFilters(filters, remoteFilters);

    // Cannot find the filter in remote filters, that means the filter is deleted
    const isDeleted = !remoteFilters.find((it) => it.fid === currentAppliedFilter.fid);
    if (isDeleted) {
      const newFilter = { ...filters[0] };
      delete newFilter.fid;
      set(dashboardInfo, appliedFiltersPath, [newFilter]);
    } else {
      set(dashboardInfo, appliedFiltersPath, newAppliedFilters);
    }

    // update drilldown filter
    const drilldownFilters = newAppliedFilters.map((it) => {
      const tRules = getRulesFromFilter(it.rule, true);
      const tFilter = getFilterFromRules(tRules, true);
      return {
        name: it.name || tFilter.name,
        rule: tFilter.rule,
      };
    });
    set(dashboardInfo, summaryDrilldownDataPath, [initialFilter, ...drilldownFilters]);
  }
};

const updateFiltersForNOC = async (dashboardInfo: Record<string, any>) => {
  const appliedFilterPath = 'config.base.filters.appliedFilter';
  const trendsAppliedFiltersPath = 'config.base.trends.appliedFilter';

  const appliedFilter = get(dashboardInfo, appliedFilterPath, []);
  const trendsFilters = get(dashboardInfo, trendsAppliedFiltersPath, []);

  // collect all the filter id to get remote filter one time.
  const filterIds = getFids([
    ...(appliedFilter?.length ? appliedFilter : []),
    ...(trendsFilters?.length ? trendsFilters : []),
  ]);

  const remoteFilters = await getRemoteFilters(filterIds);

  const shouldUpdateAppliedFilter = Array.isArray(appliedFilter) && appliedFilter.length;
  if (shouldUpdateAppliedFilter) {
    set(dashboardInfo, appliedFilterPath, mergeWithRemoteFilters(appliedFilter, remoteFilters));
  }

  const shouldUpdateTrendsFilters = Array.isArray(trendsFilters) && trendsFilters.length;
  if (shouldUpdateTrendsFilters) {
    set(
      dashboardInfo,
      trendsAppliedFiltersPath,
      mergeWithRemoteFilters(trendsFilters, remoteFilters),
    );
  }
};

const useGetSavedDashboard = (dashboardId, applySavedData) => {
  const [isSavedDataApplied, setIsSavedDataApplied] = useState(false);
  const dispatch = useDispatch();
  const { baseUrl } = window.conviva;
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [SAVED_DASHBOARDS_BASE_KEY, dashboardId],
    queryFn: async () => {
      const { data: dashboardInfo } = await getDashboardById(dashboardId);
      try {
        const dashboardType = get(dashboardInfo, 'dashboardType.id');
        if ([DASHBOARD_TYPE.TRENDS, DASHBOARD_TYPE.APP_EXPERIENCE].includes(dashboardType)) {
          await updateFiltersForTrends(dashboardInfo);
        } else if (dashboardType === DASHBOARD_TYPE.NOC) {
          await updateFiltersForNOC(dashboardInfo);
        }
      } catch (e) {
        /* do nothing */
      }
      return dashboardInfo;
    },
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    onSuccess: (data) => {
      applySavedData(data);
      setIsSavedDataApplied(true);

      // init store
      const saveStore = { meta: {}, config: {}, initialSavedData: {} };
      if (data) {
        const { id, name, isPublic, creatorId } = data;
        saveStore.meta = {
          id,
          name,
          isPublic,
          creatorId,
          isSaved: true,
          hasChanged: false,
        };
        saveStore.initialSavedData = data;
      }
      updateSaveData({ pageKey: dashboardId, state: saveStore });
    },
    onError: () => {
      setIsSavedDataApplied(false);
      if (isAppModule) {
        navigate(`${baseUrl}/dashboard/app-experience`, { replace: true });
      } else {
        navigate(`${baseUrl}/dashboard/trends`, { replace: true });
      }
    },
  });

  return {
    ready: !isLoading && isSavedDataApplied,
    isError,
    savedData: data,
    refetch,
  };
};

export default useGetSavedDashboard;
